import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RestaurantMenu from '../ui/RestaurantMenu';
import { useWineAppState } from '../../hooks/useWineAppState';
import { fetchRestaurantMenuData } from '../../utils/menuApi';
import logo from '../../assets/wineapp-logo.png';

const RestaurantMenuPage = () => {
  const {
    diningLocation,
    restaurant,
    restaurantMenu,
    isLoadingRestaurantMenu,
    errorFetchingRestaurantMenu,
    restaurantSummary,
    isLoadingSummaries,
    errorFetchingSummaries,
    resetApp,
    setRestaurantMenu,
    setIsLoadingRestaurantMenu,
    setErrorFetchingRestaurantMenu,
  } = useWineAppState();

  const navigate = useNavigate();

  useEffect(() => {
    if (!restaurant) {
      navigate('/welcome', { replace: true });
      return;
    }

    if (diningLocation !== 'eating_out') {
      navigate('/recommendations', { replace: true });
      return;
    }

    if (
      (restaurantMenu && restaurantMenu.restaurant === restaurant) ||
      isLoadingRestaurantMenu
    ) {
      return;
    }

    let cancelled = false;

    const loadMenu = async () => {
      setIsLoadingRestaurantMenu(true);
      setErrorFetchingRestaurantMenu(null);
      setRestaurantMenu(null);

      try {
        const data = await fetchRestaurantMenuData(restaurant);
        if (!cancelled) {
          setRestaurantMenu(data);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error?.message || 'Error loading menu. Please try again.';
          setErrorFetchingRestaurantMenu(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRestaurantMenu(false);
        }
      }
    };

    loadMenu();

    return () => {
      cancelled = true;
    };
  }, [restaurant, diningLocation, restaurantMenu, isLoadingRestaurantMenu, navigate, setErrorFetchingRestaurantMenu, setIsLoadingRestaurantMenu, setRestaurantMenu]);

  const handleRetry = async () => {
    if (!restaurant) {
      return;
    }

    setIsLoadingRestaurantMenu(true);
    setErrorFetchingRestaurantMenu(null);

    try {
      const data = await fetchRestaurantMenuData(restaurant);
      setRestaurantMenu(data);
    } catch (error) {
      const message = error?.message || 'Error loading menu. Please try again.';
      setErrorFetchingRestaurantMenu(message);
    } finally {
      setIsLoadingRestaurantMenu(false);
    }
  };

  const handleBackToRecommendations = () => {
    navigate('/recommendations');
  };

  const handleStartOver = () => {
    resetApp();
    navigate('/welcome');
  };

  const canShowMenu =
    !isLoadingRestaurantMenu &&
    !errorFetchingRestaurantMenu &&
    restaurantMenu &&
    restaurantMenu.restaurant === restaurant &&
    Array.isArray(restaurantMenu.categories) &&
    restaurantMenu.categories.length > 0;

  return (
    <div className="App">
      <div className="wine-recommendations-container">
        <div className="step-container menu-page-container">
          {restaurant && (
            <div className="restaurant-overview-section">
              <h2>Restaurant Overview</h2>
              {isLoadingSummaries ? (
                <p className="message-text">Loading restaurant information...</p>
              ) : errorFetchingSummaries ? (
                <p className="message-text error-text">Failed to load restaurant information.</p>
              ) : restaurantSummary ? (
                <div className="restaurant-summary">
                  <h3>About {restaurant}</h3>
                  <p>{restaurantSummary}</p>
                </div>
              ) : null}
            </div>
          )}

          <div className="menu-page-header">
            <h2>{restaurant ? `Wine Menu at ${restaurant}` : 'Wine Menu'}</h2>
            <p className="message-text">
              Explore every bottle and glass option available at the restaurant.
            </p>
          </div>

          {isLoadingRestaurantMenu && (
            <p className="message-text">Loading menu…</p>
          )}

          {errorFetchingRestaurantMenu && (
            <div className="menu-page-error">
              <p className="message-text error-text">{errorFetchingRestaurantMenu}</p>
              <button className="secondary-button" onClick={handleRetry} disabled={isLoadingRestaurantMenu}>
                Try Again
              </button>
            </div>
          )}

          {canShowMenu ? (
            <RestaurantMenu menuData={restaurantMenu} />
          ) : (
            !isLoadingRestaurantMenu &&
            !errorFetchingRestaurantMenu && (
              <p className="message-text">No menu data available for this restaurant yet.</p>
            )
          )}

          <div className="navigation-buttons menu-page-actions">
            <button onClick={handleBackToRecommendations}>
              Back to Recommendations
            </button>
            <button
              onClick={handleRetry}
              disabled={isLoadingRestaurantMenu || !restaurant}
            >
              {isLoadingRestaurantMenu ? 'Refreshing…' : 'Refresh Menu'}
            </button>
            <button onClick={handleStartOver}>
              Start Over
            </button>
          </div>
        </div>
      </div>

      <div className="floating-logo">
        <Link to="/welcome">
          <img src={logo} alt="WineApp Logo" className="logo-image" />
        </Link>
      </div>

      <footer className="app-footer">
        <Link to="/about" className="about-link">About</Link>
      </footer>
    </div>
  );
};

export default RestaurantMenuPage;
